import { useState, useEffect } from 'react';
import { DownOutlined, RestOutlined, FireOutlined, PlusOutlined, MinusOutlined, CloseOutlined, EllipsisOutlined, CheckOutlined } from '@ant-design/icons';
import * as api from '../../services/api';
import './Workouts.css';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const BODY_PARTS = ['Chest', 'Back', 'Legs', 'Shoulders', 'Arms', 'Core', 'Cardio'];

function getTodayIndex() {
  return new Date().getDay();
}

export function Workouts() {
  const [selectedDay, setSelectedDay] = useState(getTodayIndex());
  const [isOpen, setIsOpen] = useState(false);
  const [restDays, setRestDays] = useState<Record<number, boolean>>({});
  const [workouts, setWorkouts] = useState<api.Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWorkoutId, setEditingWorkoutId] = useState<number | null>(null);
  const [workoutName, setWorkoutName] = useState('');
  const [sets, setSets] = useState(3);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Menu state
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [updatingWorkoutId, setUpdatingWorkoutId] = useState<number | null>(null);
  const [existingDays, setExistingDays] = useState<number[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const isRestDay = restDays[selectedDay] || false;

  // Fetch rest days on mount
  useEffect(() => {
    api.getRestDays().then(days => {
      const restDaysMap: Record<number, boolean> = {};
      days.forEach(d => restDaysMap[d] = true);
      setRestDays(restDaysMap);
    }).catch(console.error);
  }, []);

  // Fetch workouts when day changes
  useEffect(() => {
    setLoading(true);
    setMenuOpenId(null);
    api.getWorkouts(selectedDay)
      .then(setWorkouts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedDay]);

  // When menu opens, fetch days where this workout exists
  useEffect(() => {
    if (menuOpenId) {
      const workout = workouts.find(w => w.id === menuOpenId);
      if (workout) {
        api.getWorkoutDays(workout.name)
          .then(res => setExistingDays(res.days))
          .catch(console.error);
      }
    } else {
      setExistingDays([]);
    }
  }, [menuOpenId, workouts]);

  const toggleRestDay = async () => {
    const newIsRest = !isRestDay;
    setRestDays(prev => ({ ...prev, [selectedDay]: newIsRest }));
    try {
      await api.setRestDay(selectedDay, newIsRest);
    } catch (err) {
      // Revert on error
      setRestDays(prev => ({ ...prev, [selectedDay]: !newIsRest }));
      console.error(err);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const resetModal = () => {
    setWorkoutName('');
    setSets(3);
    setSelectedTags([]);
    setError('');
    setEditingWorkoutId(null);
  };

  const handleClose = () => {
    setShowModal(false);
    resetModal();
  };

  const handleSave = async () => {
    if (!workoutName.trim()) {
      setError('Please enter a workout name');
      return;
    }

    const trimmedName = workoutName.trim();
    setSaving(true);
    setError('');

    try {
      if (editingWorkoutId) {
        const current = workouts.find(w => w.id === editingWorkoutId);
        if (!current) {
          setError('Workout not found');
          return;
        }

        const updated = await api.updateWorkout(editingWorkoutId, {
          name: trimmedName,
          sets,
          bodyParts: selectedTags,
          day: current.day,
        });

        setWorkouts(prev => prev.map(w => w.id === editingWorkoutId ? updated : w));
      } else {
        const result = await api.createWorkout({
          name: trimmedName,
          sets,
          bodyParts: selectedTags,
          day: selectedDay
        });

        setWorkouts(prev => [...prev, result]);
      }
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save workout');
    } finally {
      setSaving(false);
    }
  };

  const handleEditWorkout = (workout: api.Workout) => {
    setEditingWorkoutId(workout.id);
    setWorkoutName(workout.name);
    setSets(workout.sets);
    setSelectedTags(workout.bodyParts);
    setError('');
    setShowModal(true);
    setMenuOpenId(null);
  };

  const handleToggleMenu = (id: number) => {
    setMenuOpenId(prev => prev === id ? null : id);
  };

  const handleChangeSets = async (id: number, delta: number) => {
    const workout = workouts.find(w => w.id === id);
    if (!workout) return;

    const newSets = Math.max(1, workout.sets + delta);
    if (newSets === workout.sets) return;

    setUpdatingWorkoutId(id);

    // Optimistic update
    setWorkouts(prev => prev.map(w =>
      w.id === id ? { ...w, sets: newSets } : w
    ));

    try {
      await api.updateWorkout(id, {
        name: workout.name,
        sets: newSets,
        bodyParts: workout.bodyParts,
        day: workout.day
      });
    } catch (err) {
      // Revert on error
      setWorkouts(prev => prev.map(w =>
        w.id === id ? { ...w, sets: workout.sets } : w
      ));
      console.error(err);
    } finally {
      setUpdatingWorkoutId(null);
    }
  };

  const handleMoveWorkout = async (id: number, newDay: number) => {
    const workout = workouts.find(w => w.id === id);
    if (!workout || newDay === workout.day) return;

    // Check if already exists on target day
    if (existingDays.includes(newDay)) {
      return; // Already exists, don't move
    }

    setUpdatingWorkoutId(id);

    try {
      await api.updateWorkout(id, {
        name: workout.name,
        sets: workout.sets,
        bodyParts: workout.bodyParts,
        day: newDay
      });
      // Remove from current list since it moved to another day
      setWorkouts(prev => prev.filter(w => w.id !== id));
      setMenuOpenId(null);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingWorkoutId(null);
    }
  };

  const handleDeleteWorkout = async (id: number) => {
    const workout = workouts.find(w => w.id === id);
    if (!workout) return;

    if (deleteInput.trim() !== workout.name) {
      setDeleteError('Name does not match.');
      return;
    }

    setUpdatingWorkoutId(id);
    try {
      await api.deleteWorkout(id);
      setWorkouts(prev => prev.filter(w => w.id !== id));
      setMenuOpenId(null);
      setDeleteConfirmId(null);
      setDeleteInput('');
      setDeleteError('');
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingWorkoutId(null);
    }
  };

  return (
    <div className="workouts-page">
      {/* Click overlay to close menu */}
      {menuOpenId && (
        <div className="card-menu-overlay" onClick={() => setMenuOpenId(null)} />
      )}

      <div className="day-row">
        <div className="day-selector">
        <button className="day-dropdown" onClick={() => setIsOpen(!isOpen)}>
          <span>{DAYS[selectedDay]}</span>
          <DownOutlined className={`arrow ${isOpen ? 'open' : ''}`} />
        </button>
        {isOpen && (
          <div className="day-options">
            {DAYS.map((day, index) => (
              <button
                key={day}
                className={`day-option ${index === selectedDay ? 'active' : ''}`}
                onClick={() => {
                  setSelectedDay(index);
                  setIsOpen(false);
                }}
              >
                {day}
                {index === getTodayIndex() && <span className="today-badge">Today</span>}
              </button>
            ))}
          </div>
        )}
        </div>

        <div className="rest-toggle">
          <span className="rest-label">Rest Day</span>
          <button
            className={`toggle-switch ${isRestDay ? 'on' : ''}`}
            onClick={toggleRestDay}
          >
            <span className="toggle-knob" />
          </button>
        </div>
      </div>

      {!isRestDay && (
        <div className="workouts-content">
          {loading ? (
            <div className="empty-state">
              <p>Loading...</p>
            </div>
          ) : workouts.length === 0 ? (
            <div className="empty-state">
              <FireOutlined className="empty-icon" />
              <p>No workouts added</p>
            </div>
          ) : (
            <div className="workout-list">
              {workouts.map(workout => {
                const isMenuOpen = menuOpenId === workout.id;

                return (
                  <div key={workout.id} className="workout-card">
                    <div className="workout-main">
                      <div className="workout-info">
                        <div className="workout-header">
                          <h4>{workout.name}</h4>
                          <button
                            className="menu-btn"
                            onClick={() => handleToggleMenu(workout.id)}
                          >
                            <EllipsisOutlined />
                          </button>
                        </div>
                        <div className="workout-meta">
                          <div className="sets-control inline">
                            <button
                              className="sets-btn"
                              onClick={() => handleChangeSets(workout.id, -1)}
                              disabled={updatingWorkoutId === workout.id}
                            >
                              <MinusOutlined />
                            </button>
                            <span className="sets-value">{workout.sets} sets</span>
                            <button
                              className="sets-btn"
                              onClick={() => handleChangeSets(workout.id, 1)}
                              disabled={updatingWorkoutId === workout.id}
                            >
                              <PlusOutlined />
                            </button>
                          </div>
                          {workout.bodyParts.length > 0 && (
                            <span className="workout-tags">
                              {workout.bodyParts.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {isMenuOpen && (
                      <div className="card-menu">
                        <div className="menu-section">Move to</div>
                        <div className="menu-days">
                          {DAYS.map((day, index) => {
                            const isCurrent = index === workout.day;
                            const alreadyExists = existingDays.includes(index) && !isCurrent;

                            return (
                              <button
                                key={day}
                                className={`menu-day ${isCurrent ? 'current' : ''} ${alreadyExists ? 'exists' : ''}`}
                                onClick={() => !alreadyExists && handleMoveWorkout(workout.id, index)}
                                disabled={updatingWorkoutId === workout.id || alreadyExists}
                              >
                                <span>{day}</span>
                                {isCurrent && <span className="menu-badge">Current</span>}
                                {alreadyExists && <span className="menu-badge exists"><CheckOutlined /> Exists</span>}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          className="menu-edit"
                          onClick={() => handleEditWorkout(workout)}
                          disabled={updatingWorkoutId === workout.id}
                        >
                          Edit workout
                        </button>
                        {deleteConfirmId === workout.id ? (
                          <div className="delete-confirm">
                            <label>Type the workout name to confirm</label>
                            <input
                              type="text"
                              value={deleteInput}
                              onChange={e => {
                                setDeleteInput(e.target.value);
                                setDeleteError('');
                              }}
                              placeholder={workout.name}
                              disabled={updatingWorkoutId === workout.id}
                            />
                            {deleteError && <span className="delete-error">{deleteError}</span>}
                            <div className="delete-actions">
                              <button
                                className="btn-secondary"
                                onClick={() => {
                                  setDeleteConfirmId(null);
                                  setDeleteInput('');
                                  setDeleteError('');
                                }}
                                disabled={updatingWorkoutId === workout.id}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn-danger"
                                onClick={() => handleDeleteWorkout(workout.id)}
                                disabled={updatingWorkoutId === workout.id}
                              >
                                Confirm delete
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="menu-delete"
                            onClick={() => {
                              setDeleteConfirmId(workout.id);
                              setDeleteInput('');
                              setDeleteError('');
                            }}
                            disabled={updatingWorkoutId === workout.id}
                          >
                            <CloseOutlined /> Delete workout
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isRestDay && (
        <div className="rest-content">
          <RestOutlined className="rest-icon" />
          <p>Rest day - recover and recharge!</p>
        </div>
      )}

      {!isRestDay && (
        <button
          className={`fab ${showModal ? 'fab-hidden' : ''}`}
          onClick={() => {
            resetModal();
            setShowModal(true);
          }}
        >
          <PlusOutlined className="fab-plus" />
          <FireOutlined className="fab-fire" />
        </button>
      )}

      <div className={`modal-overlay ${showModal ? 'show' : ''}`} onClick={handleClose} />

      <div className={`add-workout-modal ${showModal ? 'show' : ''}`}>
        <div className="modal-header">
          <h3>{editingWorkoutId ? 'Edit Workout' : 'Add Workout'}</h3>
          <button className="close-btn" onClick={handleClose}>
            <CloseOutlined />
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="input-group">
            <label>Workout Name</label>
            <input
              type="text"
              placeholder="e.g. Bench Press"
              value={workoutName}
              onChange={(e) => setWorkoutName(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Sets</label>
            <div className="sets-control">
              <button
                className="sets-btn"
                onClick={() => setSets(prev => Math.max(1, prev - 1))}
              >
                <MinusOutlined />
              </button>
              <span className="sets-value">{sets}</span>
              <button
                className="sets-btn"
                onClick={() => setSets(prev => prev + 1)}
              >
                <PlusOutlined />
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>Body Part</label>
            <div className="tags-container">
              {BODY_PARTS.map(tag => (
                <button
                  key={tag}
                  className={`tag ${selectedTags.includes(tag) ? 'selected' : ''}`}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          className="save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? editingWorkoutId ? 'Saving...' : 'Adding...'
            : editingWorkoutId ? 'Save Changes' : 'Add Workout'}
        </button>
      </div>
    </div>
  );
}
